import React from "react";
import { Link } from "react-router-dom";

const pillLabel = {
  news: "NEWS",
  whitepaper: "WHITEPAPER & REPORTS",
  article: "ARTICLES",
};

const news = [
  {
    slug: "prompt-engineering-meets-security-article",
    type: "news",
    title: "Prompt Engineering Meets Security: The Overlooked Risk in GenAI",
    description:
      "Most organisations treat prompt engineering as a productivity discipline — but the input channel itself is an attack surface. This article examines direct and indirect prompt injection, jailbreaking, and data exfiltration through agentic AI systems, and sets out what a prompt security programme looks like in practice.",
    image:
      "https://images.pexels.com/photos/5380642/pexels-photo-5380642.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "ai-governance-2026",
    type: "news",
    title: "2026: A Landmark Year for Global AI Governance and Ethical Oversight",
    description:
      "2026 marks the full-force application of the EU AI Act, accelerating US federal legislative momentum, and mature enforcement of China’s generative AI regulations. This article maps the converging governance landscape and sets out what organisations must do now to meet binding compliance obligations across multiple jurisdictions.",
    image:
      "https://images.pexels.com/photos/1181400/pexels-photo-1181400.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "agentic-modeling-breakthroughs",
    type: "news",
    title: "Advancements in Agentic Modeling: 2024–2025 Breakthroughs Drive AI Autonomy",
    description:
      "Extended reasoning models, standardised tool use, computer-use APIs, and multi-agent orchestration frameworks have crossed the reliability threshold for enterprise deployment. This article explains what changed, what it means for practitioners, and why the security community cannot wait for agentic capabilities to stabilise before building defences.",
    image:
      "https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

const whitepapers = [
  {
    slug: "prompt-engineering-meets-security-whitepaper",
    type: "whitepaper",
    title: "Prompt Engineering Meets Security: The Overlooked Risk in GenAI",
    description:
      "A technical whitepaper covering the full prompt attack taxonomy — direct injection, indirect injection, jailbreaking, and model inversion — alongside a structured risk assessment framework and defensive architectures including system prompt hardening, privilege separation, semantic input validation, and continuous red-teaming. Intended for security architects, AI platform engineers, and CISO offices.",
    image:
      "https://images.pexels.com/photos/6476584/pexels-photo-6476584.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "offensive-ai-security",
    type: "whitepaper",
    title: "Offensive AI Security: Pioneering Tools for Next-Gen Defense",
    description:
      "AI-powered adversaries operate at machine speed, adapting in real time to defensive measures. This whitepaper examines offensive AI security as a discipline: automated vulnerability discovery, adversarial machine learning, automated red-teaming for LLM applications, and AI-powered social engineering simulation — with a framework for building an offensive programme that out-paces attackers.",
    image:
      "https://images.pexels.com/photos/5380642/pexels-photo-5380642.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "trifort-framework",
    type: "whitepaper",
    title: "TriFort AI Security Framework",
    description:
      "RakFort’s purpose-built methodology for assessing, building, and operating AI security programmes. TriFort organises AI security across three mutually reinforcing layers — Detect, Defend, and Disrupt — with a five-phase implementation methodology, a five-level maturity model, and alignment mappings to the EU AI Act, NIST AI RMF, and OWASP LLM Top 10.",
    image:
      "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

const blogArticles = [
  {
    slug: "red-teaming-genai",
    type: "article",
    title: "Navigating the Shadows: Red-Teaming GenAI Systems",
    description:
      "Red-teaming a GenAI system is not like penetration testing a web application — there is no CVE list, no fixed attack signatures, and the model is a moving target. This post covers what makes GenAI red-teaming different, how to structure an effective exercise, when and how to automate at scale, and how to turn findings into actionable, verified fixes.",
    image:
      "https://images.pexels.com/photos/5380664/pexels-photo-5380664.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "ai-native-security-teams",
    type: "article",
    title: "Fortifying the Future: Building AI-Native Security Teams",
    description:
      "AI security threats require expertise that sits at the intersection of LLM architecture, prompt design, and adversarial mindset — a combination the current talent market has in short supply. This post examines the skills that matter, the internal-development-versus-external-hiring trade-offs, organisational structure patterns, and the culture required to sustain an effective AI-native security function.",
    image:
      "https://images.pexels.com/photos/1181671/pexels-photo-1181671.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
  {
    slug: "legacy-cyber-to-aisecops",
    type: "article",
    title: "From Legacy Cyber to AISecOps",
    description:
      "The traditional SOC — built for network perimeters, known signatures, and human-paced threats — is overwhelmed by AI-augmented adversaries and blind to the telemetry that AI systems generate. This post sets out what AISecOps means in practice, a three-phase transition path from legacy SOC to unified AI-aware operations, and how to make the case for sustained investment to leadership.",
    image:
      "https://images.pexels.com/photos/3861964/pexels-photo-3861964.jpeg?auto=compress&cs=tinysrgb&w=1200",
  },
];

function ResourceCard({ slug, type, title, subtitle, description, image }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      {image && (
        <div className="relative h-52 w-full overflow-hidden">
          <img
            src={image}
            alt={title}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute left-4 top-4 inline-flex items-center rounded border border-slate-200 bg-white/95 text-[#002b5c] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.20em]">
            {pillLabel[type]}
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col px-6 py-6">
        {!image && (
          <span className="mb-3 inline-flex w-max items-center rounded border border-slate-200 bg-slate-50 text-[#002b5c] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.20em]">
            {pillLabel[type]}
          </span>
        )}

        <h3 className="text-lg font-semibold leading-snug text-slate-900">
          {title}
        </h3>
        {subtitle && (
          <p className="mt-1 text-sm font-medium text-slate-600">
            {subtitle}
          </p>
        )}

        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {description}
        </p>

        {slug && (
          <Link
            to={`/resources/${slug}`}
            className="mt-6 inline-flex w-max items-center text-[11px] font-semibold uppercase tracking-[0.25em] text-[#002b5c] hover:text-[#001f45]"
          >
            Read more&nbsp;»
          </Link>
        )}
      </div>
    </article>
  );
}

export default function ResourcesPage() {
  return (
    <div className="bg-white text-[#2c2c2c] overflow-x-hidden">
      <header className="mx-auto max-w-6xl px-5 pt-10 pb-12 lg:px-0 text-center md:text-left">
        <div className="inline-flex items-center rounded border border-slate-200 bg-white px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#002b5c] mb-4 shadow-sm">
          Resources
        </div>
        <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
          Insights, Research &{" "}
          <span className="text-[#002b5c]">
            AI Security Updates
          </span>
        </h1>
        <p className="mt-4 max-w-2xl text-sm md:text-base text-slate-600">
          Stay ahead of the threat curve with RakFort’s news, whitepapers and
          in-depth analysis across AI security, governance, and AISecOps.
        </p>
      </header>

      {/* NEWS */}
      <section className="bg-[#f0f7ff] border-y border-[#e5eef6] py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-0">
          <h2 className="text-2xl md:text-3xl font-semibold text-center md:text-left mb-8 text-slate-900">
            News
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {news.map((item, i) => (
              <ResourceCard key={`news-${i}`} {...item} />
            ))}
          </div>
        </div>
      </section>

      {/* WHITEPAPERS */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-0">
          <h2 className="text-2xl md:text-3xl font-semibold text-center md:text-left mb-8 text-slate-900">
            Whitepapers & Reports
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {whitepapers.map((item, i) => (
              <ResourceCard key={`wp-${i}`} {...item} />
            ))}
          </div>
        </div>
      </section>

      {/* BLOG ARTICLES */}
      <section className="bg-[#f0f7ff] border-y border-[#e5eef6] py-16">
        <div className="mx-auto max-w-6xl px-5 lg:px-0">
          <h2 className="text-2xl md:text-3xl font-semibold text-center md:text-left mb-8 text-slate-900">
            Blog Articles
          </h2>
          <div className="grid gap-8 md:grid-cols-3">
            {blogArticles.map((item, i) => (
              <ResourceCard key={`blog-${i}`} {...item} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
